import { createTheme, type MantineColorsTuple } from "@mantine/core";

/**
 * Single source of truth for the visual language.
 *
 * The look is dark-first, high-contrast minimalism with an indigo/violet accent.
 * Component `defaultProps` here keep every page consistent without repeating
 * style props (radius / border / padding) across the codebase.
 */

// Indigo → violet accent scale (light → dark).
const brand: MantineColorsTuple = [
  "#f5f3ff",
  "#ede9fe",
  "#ddd6fe",
  "#c4b5fd",
  "#a78bfa",
  "#8b5cf6",
  "#7c3aed",
  "#6d28d9",
  "#5b21b6",
  "#4c1d95",
];

// Near-black surface scale for a modern "techno" dark theme.
const dark: MantineColorsTuple = [
  "#c9c9d4", // 0 — primary text
  "#a7a8b8", // 1
  "#8d8fa3", // 2 — dimmed text
  "#5e6175", // 3
  "#3c3f52", // 4 — borders
  "#262835", // 5 — raised / hover surface
  "#1a1b22", // 6 — card / surface
  "#101117", // 7 — body background
  "#0b0c11", // 8
  "#070709", // 9
];

export const theme = createTheme({
  primaryColor: "brand",
  primaryShade: { light: 6, dark: 5 },
  autoContrast: true,
  cursorType: "pointer",
  defaultRadius: "md",
  colors: { brand, dark },
  fontFamily: "Manrope, sans-serif",
  fontFamilyMonospace: "ui-monospace, SFMono-Regular, Menlo, monospace",
  headings: {
    fontFamily: "Manrope, sans-serif",
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "2.25rem", lineHeight: "1.15", fontWeight: "800" },
      h2: { fontSize: "1.625rem", lineHeight: "1.2", fontWeight: "700" },
      h3: { fontSize: "1.25rem", lineHeight: "1.3", fontWeight: "700" },
      h4: { fontSize: "1.05rem", lineHeight: "1.35", fontWeight: "600" },
    },
  },
  shadows: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.18)",
    sm: "0 2px 8px -2px rgba(0, 0, 0, 0.25)",
    md: "0 8px 24px -8px rgba(0, 0, 0, 0.35)",
    lg: "0 16px 40px -12px rgba(0, 0, 0, 0.45)",
  },
  components: {
    Card: { defaultProps: { radius: "lg", withBorder: true, padding: "lg" } },
    Paper: { defaultProps: { radius: "lg" } },
    Button: { defaultProps: { radius: "md" } },
    Badge: { defaultProps: { radius: "sm" } },
    Modal: { defaultProps: { radius: "lg", centered: true } },
    SegmentedControl: { defaultProps: { radius: "md" } },
    TextInput: { defaultProps: { radius: "md" } },
    Select: { defaultProps: { radius: "md" } },
    ColorInput: { defaultProps: { radius: "md" } },
    Alert: { defaultProps: { radius: "md" } },
  },
});
